// ReassignProjectRoleModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { reAssign } from '../services/project/userService';
import { getRoles } from '../services/project/roleService';

/**
 * Props:
 * - show: boolean
 * - onClose: () => void
 * - onReassign: () => void
 * - user: { id, firstName, lastName, roles?: [{ id, name, projectId, project? }] }
 * - projects: [{ id, name }]   // pass from context/useProjects() or a prop
 * - initialProjectId?: number|string (optional)
 * - hideAdmin?: boolean (default true)
 */
const ReassignProjectRoleModal = ({
  show,
  onClose,
  onReassign,
  user,
  projects = [],
  initialProjectId,
  hideAdmin = true,
}) => {
  const [projectId, setProjectId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Derive a sensible default project when modal opens or user changes
  useEffect(() => {
    if (!show) return;
    // Prefer explicit initialProjectId, otherwise use user's first role project, otherwise the first project in list
    const defaultPid =
      initialProjectId ??
      user?.roles?.[0]?.projectId ??
      (projects.length ? projects[0].id : '');

    setProjectId(String(defaultPid || ''));
    setRoleId(''); // reset role whenever modal (re)opens
  }, [show, user, initialProjectId, projects]);

  // Fetch roles whenever project changes
  useEffect(() => {
    const fetch = async () => {
      if (!projectId) {
        setRoleOptions([]);
        setRoleId('');
        return;
      }
      setLoadingRoles(true);
      try {
        const { roles } = await getRoles(projectId);
        let opts = roles || [];
        if (hideAdmin) opts = opts.filter(r => r.name?.toLowerCase() !== 'admin');
        setRoleOptions(opts);

        // Try defaulting to the user's current role in this project if present
        const usersRoleInProject = user?.roles?.find(r => String(r.projectId) === String(projectId));
        if (usersRoleInProject && opts.some(o => String(o.id) === String(usersRoleInProject.id))) {
          setRoleId(String(usersRoleInProject.id));
        } else {
          setRoleId(''); // force explicit selection
        }
      } catch (error) {
        console.error('Load roles error:', error);
        toast.error(error?.response?.data?.errorMessage || 'Failed to load roles for this project');
        setRoleOptions([]);
        setRoleId('');
      } finally {
        setLoadingRoles(false);
      }
    };
    fetch();
  }, [projectId, user, hideAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || !roleId) {
      toast.error('Please select both project and role.');
      return;
    }
    try {
      const { success, message, errorMessage } = await reAssign(user.id, roleId);
      if (!success) {
        toast.error(errorMessage || 'Something went wrong');
        return;
      }
      toast.success(message || 'Reassigned successfully');
      onReassign?.();
      onClose?.();
    } catch (error) {
      console.error('Reassign error:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  return (
    <Modal show={show} onHide={onClose} fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Reassign&nbsp;
          <strong>{user?.firstName} {user?.lastName}</strong>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Project */}
          <Form.Group className="mb-3">
            <Form.Label>Project</Form.Label>
            <Form.Select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                // role reset handled by useEffect on projectId
              }}
            >
              <option value="">Select Project</option>
              {(projects || []).map(p => (
                <option key={p.id} value={p.id}>{p.name || `Project #${p.id}`}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Role */}
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                disabled={!projectId || loadingRoles}
              >
                <option value="">{loadingRoles ? 'Loading roles…' : 'Select Role'}</option>
                {roleOptions
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
              </Form.Select>
              {loadingRoles && <Spinner animation="border" size="sm" />}
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button variant="secondary" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!projectId || !roleId || loadingRoles}>
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ReassignProjectRoleModal;
